import React, { useState } from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { uploadImageToCloudinary } from '../../../../app/helper/functions';
import { BlogFormProps, CategoryOption } from '../../../../app/utlis/type';
import { categoryOptions } from '../../../../app/utlis/options';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Interface for category options


// Interface for hashtag options (same structure)
interface HashtagOption {
  value: string;
  label: string;
}

// Sample category options



const BlogForm: React.FC<BlogFormProps> = ({ onSubmit, initialData, isUpdating = false }) => {
  const [title, setTitle] = useState(initialData?.title || '');
  const [subtitle, setSubTitle] = useState(initialData?.subtitle || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [selectedCategories, setSelectedCategories] = useState<CategoryOption[]>(
    initialData?.categories
      ? initialData.categories.map(cat => ({
        value: cat,
        label: cat.charAt(0).toUpperCase() + cat.slice(1) // Capitalize first letter
      }))
      : []
  );
  const [selectedHashtags, setSelectedHashtags] = useState<HashtagOption[]>(
    initialData?.hashtags
      ? initialData.hashtags.map(tag => ({
        value: tag,
        label: tag.startsWith('#') ? tag : `#${tag}`
      }))
      : []
  );
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(initialData?.imageUrl || null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({
    title: '',
    subtitle: '',
    description: '',
    categories: '',
    image: '',
    hashtags: ''
  });

  // Handle image selection
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Check if file is an image
    if (!file.type.match('image.*')) {
      setErrors(prev => ({ ...prev, image: 'Please select an image file' }));
      return;
    }

    // Check file size (limit to 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image size should be less than 5MB' }));
      return;
    }

    setImageFile(file);
    setErrors(prev => ({ ...prev, image: '' }));

    // Create a preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleCreateHashtag = (inputValue: string) => {
    // Remove # if it exists at the beginning
    const value = inputValue.startsWith('#') ? inputValue.substring(1) : inputValue;

    // Create a new option from the input
    const newHashtag = { value, label: `#${value}` };
    setSelectedHashtags(prev => [...prev, newHashtag]);
    return newHashtag;
  };

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      title: '',
      subtitle: '',
      description: '',
      categories: '',
      image: '',
      hashtags: ''
    };

    if (!title.trim()) {
      newErrors.title = 'Title is required';
      isValid = false;
    }

    if (!subtitle.trim()) {
      newErrors.subtitle = 'Title is required';
      isValid = false;
    }

    if (!description.trim()) {
      newErrors.description = 'Description is required';
      isValid = false;
    }

    if (selectedCategories.length === 0) {
      newErrors.categories = 'Please select at least one category';
      isValid = false;
    }

    // Only require image for new blogs, not when editing
    if (!imageFile && !previewUrl) {
      newErrors.image = 'Please upload an image';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);

    // Create FormData to send files and other data
    const formData = new FormData();
    formData.append('title', title);
    formData.append('subtitle', subtitle);
    formData.append('description', description);
    formData.append('categories', JSON.stringify(selectedCategories.map(cat => cat.value)));
    formData.append('hashtags', JSON.stringify(selectedHashtags.map(tag => tag.value)));

    try {
      // Only upload a new image if the user selected a new one
      if (imageFile && previewUrl && !previewUrl.startsWith('http')) {
        const uploadResult = await uploadImageToCloudinary(previewUrl);
        if (!uploadResult) {
          throw new Error('Failed to upload image');
        }
        const imageUrl =
          typeof uploadResult === 'string' ? uploadResult : uploadResult.url;
        formData.append('imageUrl', imageUrl);
      } else if (initialData?.imageUrl && !imageFile) {
        // If we're editing and no new image was selected, keep the old URL
        formData.append('imageUrl', initialData.imageUrl);
      }

      // Call the parent's onSubmit function
      onSubmit(formData);
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({ ...prev, image: 'Failed to upload image. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-blackDarkTheme rounded-lg shadow-lg p-6">
      <h2 className="text-xl font-semibold mb-4 text-white">
        {initialData ? 'Edit Blog Post' : 'Create New Blog Post'}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="title">
            Title
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setErrors(prev => ({ ...prev, title: '' }));
            }}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Blog title"
          />
          {errors.title && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="title">
            Sub Title
          </label>
          <input
            id="title"
            type="text"
            value={subtitle}
            onChange={(e) => {
              setSubTitle(e.target.value);
              setErrors(prev => ({ ...prev, subtitle: '' }));
            }}
            className="w-full px-3 py-2 border border-gray-600 bg-gray-700 text-white rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            placeholder="Blog sub title"
          />
          {errors.subtitle && <p className="mt-1 text-sm text-red-500">{errors.title}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="description">
            Description
          </label>
          <div className="bg-gray-700 rounded-md border border-gray-600 text-white focus-within:ring-indigo-500 focus-within:border-indigo-500">
            <ReactQuill
              id="description"
              value={description}
              onChange={(value) => {
                setDescription(value);
                setErrors((prev) => ({ ...prev, description: '' }));
              }}
              theme="snow" // or "bubble"
              className="text-white"
            />
          </div>
          {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="categories">
            Categories
          </label>
          <Select
            id="categories"
            isMulti
            options={categoryOptions}
            value={selectedCategories}
            onChange={(selected) => {
              setSelectedCategories(selected as CategoryOption[]);
              setErrors(prev => ({ ...prev, categories: '' }));
            }}
            className="react-select-container"
            classNamePrefix="react-select"
            styles={{
              // Custom styles for dark mode
              control: (base) => ({
                ...base,
                backgroundColor: '#374151',
                borderColor: '#4B5563',
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#6366F1'
                }
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: '#1F2937'
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#4F46E5'
                  : state.isFocused
                    ? '#374151'
                    : '#1F2937',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#374151'
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#4F46E5'
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: 'white'
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338CA',
                  color: 'white'
                }
              }),
              input: (base) => ({
                ...base,
                color: 'white'
              }),
              placeholder: (base) => ({
                ...base,
                color: '#9CA3AF'
              }),
              singleValue: (base) => ({
                ...base,
                color: 'white'
              }),
            }}
            placeholder="Select categories..."
          />
          {errors.categories && <p className="mt-1 text-sm text-red-500">{errors.categories}</p>}
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="hashtags">
            Hashtags
          </label>
          <CreatableSelect
            id="hashtags"
            isMulti
            value={selectedHashtags}
            onChange={(selected) => {
              setSelectedHashtags(selected as HashtagOption[]);
              setErrors(prev => ({ ...prev, hashtags: '' }));
            }}
            onCreateOption={handleCreateHashtag}
            className="react-select-container"
            classNamePrefix="react-select"
            formatCreateLabel={(inputValue) => `Add hashtag "${inputValue.startsWith('#') ? inputValue : `#${inputValue}`}"`}
            placeholder="Enter hashtags..."
            styles={{
              // Custom styles for dark mode (same as categories)
              control: (base) => ({
                ...base,
                backgroundColor: '#374151',
                borderColor: '#4B5563',
                color: 'white',
                boxShadow: 'none',
                '&:hover': {
                  borderColor: '#6366F1'
                }
              }),
              menu: (base) => ({
                ...base,
                backgroundColor: '#1F2937'
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? '#4F46E5'
                  : state.isFocused
                    ? '#374151'
                    : '#1F2937',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#374151'
                }
              }),
              multiValue: (base) => ({
                ...base,
                backgroundColor: '#4F46E5'
              }),
              multiValueLabel: (base) => ({
                ...base,
                color: 'white'
              }),
              multiValueRemove: (base) => ({
                ...base,
                color: 'white',
                '&:hover': {
                  backgroundColor: '#4338CA',
                  color: 'white'
                }
              }),
              input: (base) => ({
                ...base,
                color: 'white'
              }),
              placeholder: (base) => ({
                ...base,
                color: '#9CA3AF'
              }),
              singleValue: (base) => ({
                ...base,
                color: 'white'
              }),
            }}
          />
          {errors.hashtags && <p className="mt-1 text-sm text-red-500">{errors.hashtags}</p>}
          <p className="text-xs text-gray-400 mt-1">Type and press enter to create a new hashtag</p>
        </div>

        <div className="mb-4">
          <label className="block text-gray-300 text-sm font-medium mb-2" htmlFor="image">
            Featured Image
          </label>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <input
                id="image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />
              <label
                htmlFor="image"
                className="flex items-center justify-center px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-white bg-gray-700 hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 cursor-pointer"
              >
                {initialData?.imageUrl ? 'Change Image' : 'Browse Image'}
              </label>
            </div>
            {previewUrl && (
              <div className="w-24 h-24 relative">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-full object-cover rounded-md"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImageFile(null);
                    setPreviewUrl(null);
                  }}
                  className="absolute -top-2 -right-2 bg-red-600 rounded-full p-1 text-white text-xs"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
          {errors.image && <p className="mt-1 text-sm text-red-500">{errors.image}</p>}
          {imageFile && !previewUrl && (
            <p className="mt-1 text-sm text-gray-400">
              {imageFile.name} ({Math.round(imageFile.size / 1024)} KB)
            </p>
          )}
        </div>

        <div className="mt-6">
          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isSubmitting
              ? isUpdating
                ? 'Updating...'
                : 'Publishing...'
              : isUpdating
                ? 'Update Blog Post'
                : 'Publish Blog Post'
            }
          </button>
        </div>
      </form>
    </div>
  );
};

export default BlogForm; 